/**
 * Munshot datasource registry — API quick reference for admin docs and skills.
 * Aligned with dashboard skill datasource-registry.md (base_urls + datasources).
 */

export const TOKEN_PLACEHOLDER = "<YOUR_SESSION_TOKEN>";

export const BASE_URLS = {
  fastapi: "https://fastapi.muns.io",
  nestjs: "https://devde.muns.io",
};

export const AUTH_NOTE =
  "Every request needs Authorization: Bearer <YOUR_SESSION_TOKEN> (use the Munshot host session token — do not hard-code a personal JWT).";

export const REGISTRY_INTRO = `Dashboard implementations should use registered datasources, build URLs from base_urls + endpoint path, follow documented contracts, respect rate limits, use host-provided auth, and handle failures gracefully.`;

/** @typedef {{ name: string; type: string; required?: boolean; description: string; example?: string }} RequestField */

export const MUNSHOT_DATA_APIS = [
  {
    id: "web_search",
    name: "Web Search",
    shortDescription: "Search the public internet using Brave Search.",
    service: "fastapi",
    method: "POST",
    path: "/tools/web-search",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "query", type: "string", required: true, description: "Search query", example: "Apple earnings outlook" },
      { name: "country", type: "string", required: false, description: "Country code for localized results", example: "US" },
    ],
    bodyExample: { query: "Apple earnings outlook", country: "US" },
    cacheTtl: 300,
    rateLimit: "60/min",
  },
  {
    id: "web_reader",
    name: "Web Reader",
    shortDescription: "Read and extract content from one or more URLs.",
    service: "fastapi",
    method: "POST",
    path: "/tools/web-reader",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "urls", type: "string[]", required: true, description: "URLs to extract content from", example: '["https://example.com/article"]' },
      { name: "task", type: "string", required: false, description: "Optional extraction objective", example: "Summarize key points" },
    ],
    bodyExample: { urls: ["https://example.com/article"], task: "Summarize key points" },
    cacheTtl: 300,
    rateLimit: "60/min",
  },
  {
    id: "news_search",
    name: "News Search",
    shortDescription: "Search recent news articles.",
    service: "fastapi",
    method: "POST",
    path: "/tools/news-search",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "query", type: "string", required: true, description: "News search query", example: "Tesla production" },
      { name: "country", type: "string", required: false, description: "Country code", example: "US" },
      { name: "from_date", type: "date", required: false, description: "Start date", example: "2026-01-01" },
      { name: "to_date", type: "date", required: false, description: "End date", example: "2026-03-01" },
    ],
    bodyExample: { query: "Tesla production", country: "US", from_date: "2026-01-01", to_date: "2026-03-01" },
    cacheTtl: 180,
    rateLimit: "60/min",
  },
  {
    id: "document_search",
    name: "Document Search",
    shortDescription: "Search proprietary documents indexed in Pinecone.",
    service: "fastapi",
    method: "POST",
    path: "/tools/document-search",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "query", type: "string", required: true, description: "Search query", example: "revenue growth" },
      { name: "user_index", type: "integer", required: true, description: "User document index", example: "1" },
      { name: "ticker_symbol", type: "string|string[]", required: false, description: "Filter by ticker", example: "AAPL" },
      { name: "from_date", type: "date", required: false, description: "Start date", example: "2026-01-01" },
      { name: "to_date", type: "date", required: false, description: "End date", example: "2026-03-31" },
      { name: "categories", type: "string[]", required: false, description: "Document categories", example: '["earnings"]' },
      { name: "doc_indexes", type: "string[]", required: false, description: "Specific doc indexes", example: '["doc-1"]' },
    ],
    bodyExample: { query: "revenue growth", user_index: 1, ticker_symbol: "AAPL" },
    cacheTtl: 120,
    rateLimit: "60/min",
  },
  {
    id: "muns_chat",
    name: "Muns Chat",
    shortDescription:
      "Stream an AI answer for a dashboard question using Muns chat context, documents, tickers, and dashboard inputs.",
    service: "nestjs",
    method: "POST",
    path: "/chat/chat-muns",
    accept: "text/event-stream",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "tasks", type: "string[]", required: true, description: "User question or task list", example: '["What are key risks for AAPL?"]' },
      { name: "query_context.chatHistory", type: "object[]", required: true, description: "Prior chat messages; use [] for new query", example: "[]" },
      { name: "query_context.TICKER_SYMBOL", type: "string[]", required: false, description: "Relevant tickers", example: '["AAPL"]' },
      { name: "query_context.FROM_DATE", type: "date", required: false, description: "Analysis start date", example: "2026-01-01" },
      { name: "query_context.TO_DATE", type: "date", required: false, description: "Analysis end date", example: "2026-03-31" },
      { name: "query_context.DASHBOARD_INPUTS", type: "object[]", required: false, description: "Dashboard extraction inputs", example: "[]" },
      { name: "query_context.mode", type: "enum", required: false, description: "fast or expert (default expert)", example: "expert" },
      { name: "chat_id", type: "string", required: false, description: "Existing chat ID to continue", example: "<chat-uuid>" },
    ],
    bodyExample: {
      tasks: ["What are the key risks for AAPL this quarter?"],
      query_context: {
        chatHistory: [],
        TICKER_SYMBOL: ["AAPL"],
        FROM_DATE: "2026-01-01",
        TO_DATE: "2026-03-31",
        mode: "expert",
      },
    },
    cacheTtl: 0,
    rateLimit: "30/min",
    responseNote: "Returns text/event-stream. Check X-Chat-Id and X-Message-Id response headers.",
  },
  {
    id: "agent_run",
    name: "Agent Run",
    shortDescription:
      "Run a registered analyst agent and stream its output for dashboard generation or refresh workflows.",
    service: "nestjs",
    method: "POST",
    path: "/agents/run",
    accept: "text/event-stream",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "agent_id", type: "string", required: false, description: "Active analyst UUID (agent_id OR agent_library_id)", example: "<agent-uuid>" },
      { name: "agent_library_id", type: "string", required: false, description: "Library agent UUID", example: "<library-agent-uuid>" },
      { name: "user_query", type: "string", required: false, description: "Dashboard question or run objective", example: "Summarize recent earnings trends" },
      { name: "metadata", type: "object", required: false, description: "Run context: stock_ticker, from_date, to_date, urls, etc.", example: '{"stock_ticker":"AAPL"}' },
      { name: "DASHBOARD_INPUTS", type: "object[]", required: false, description: "Dashboard extraction inputs", example: "[]" },
      { name: "CATEGORIES", type: "string[]", required: false, description: "Categories for agent context", example: '["earnings"]' },
      { name: "WRITING_STYLES", type: "string[]", required: false, description: "Registered writing style names", example: '["brief"]' },
    ],
    bodyExample: {
      agent_library_id: "<library-agent-uuid>",
      user_query: "Summarize recent earnings trends",
      metadata: { stock_ticker: "AAPL", from_date: "2026-01-01", to_date: "2026-03-31" },
    },
    cacheTtl: 0,
    rateLimit: "20/min",
    responseNote: "Returns text/event-stream. Check X-Active-Analyst-Id and X-Analyst-Output-Id headers.",
  },
  {
    id: "dashboard_ask",
    name: "Dashboard Ask",
    shortDescription:
      "Ask the dashboard agent a question (async). Returns a job id to poll. Intended for service-token automation callers.",
    service: "nestjs",
    method: "POST",
    path: "/agents/dashboard/ask",
    accept: "*/*",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      {
        name: "dashboardIds",
        type: "integer[]",
        required: true,
        description: "Dashboard ids from dashboards-admin",
        example: "[120]",
      },
      {
        name: "question",
        type: "string",
        required: true,
        description: "Question for the dashboard library agent",
        example: "Summarize the key takeaways from this dashboard.",
      },
      {
        name: "user_index",
        type: "integer",
        required: true,
        description: "User document index (admin UI reads this from the session token)",
        example: "123",
      },
    ],
    bodyExample: {
      dashboardIds: [120],
      question: "Summarize the key takeaways from this dashboard.",
      user_index: 123,
    },
    responseExample: {
      jobId: "cfae4c92-48ea-4e9b-bbc0-862f33fec7f8",
      status: "pending",
    },
    cacheTtl: 0,
    rateLimit: "250/min",
    responseNote: "201 — Job accepted; returns { jobId, status }. Poll GET /agents/dashboard/ask/:jobId for the result.",
  },
  {
    id: "dashboard_ask_poll",
    name: "Dashboard Ask — Poll Job",
    shortDescription: "Poll a dashboard-ask job for its status and answer.",
    service: "nestjs",
    method: "GET",
    path: "/agents/dashboard/ask/{jobId}",
    accept: "*/*",
    contentType: null,
    pathParams: [
      {
        name: "jobId",
        example: "cfae4c92-48ea-4e9b-bbc0-862f33fec7f8",
        description: "Job id returned by POST /agents/dashboard/ask",
      },
    ],
    queryParams: [],
    requestFields: [
      {
        name: "jobId",
        type: "string",
        required: true,
        description: "Path param — job UUID",
        example: "cfae4c92-48ea-4e9b-bbc0-862f33fec7f8",
      },
    ],
    bodyExample: null,
    responseExample: {
      jobId: "cfae4c92-48ea-4e9b-bbc0-862f33fec7f8",
      status: "completed",
      answer: "Key takeaway summary from the agent envelope.",
      summary: "Short summary parsed from the agent response.",
      conclusion: "Conclusion parsed from the agent response.",
      rawAnswer: "Original agent text before parsing.",
    },
    cacheTtl: 0,
    rateLimit: "250/min",
    responseNote:
      "200 — Returns { jobId, status: pending|completed|failed, answer, summary, conclusion, rawAnswer }. answer/summary/conclusion are parsed from the agent envelope; rawAnswer is the original text. 404 if job not found.",
  },
  {
    id: "portfolio_list",
    name: "Portfolio List",
    shortDescription:
      "Retrieve the user's portfolio or watchlist items with stock details such as ticker, company, sector, and industry.",
    service: "nestjs",
    method: "GET",
    path: "/portfolio/list",
    accept: "application/json",
    contentType: null,
    pathParams: [],
    queryParams: [],
    requestFields: [],
    bodyExample: null,
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote: "Returns array of portfolio items with id, ticker, rank, company_name, sector, industry, etc.",
  },
  {
    id: "email_send",
    name: "Email Send (Raw)",
    shortDescription:
      "Send a transactional email (subject + HTML or plain-text body) to a single recipient for dashboard reports, editions, or briefs.",
    service: "nestjs",
    method: "POST",
    path: "/email/send/raw",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "email", type: "string", required: true, description: "Recipient email (one per request)", example: "user@example.com" },
      { name: "subject", type: "string", required: true, description: "Email subject line", example: "Your dashboard report" },
      { name: "html", type: "string", required: false, description: "HTML body — provide html OR text, not both", example: "<p>Hello</p>" },
      { name: "text", type: "string", required: false, description: "Plain-text body — mutually exclusive with html", example: "Hello" },
    ],
    bodyExample: { email: "user@example.com", subject: "Your dashboard report", html: "<p>Hello</p>" },
    cacheTtl: 0,
    rateLimit: "30/min",
  },
  {
    id: "insider_trades",
    name: "Insider Trades",
    shortDescription:
      "Fetch insider trades for a ticker as a markdown table. Routes to Indian exchange data (NSE/BSE/Trendlyne) or US Finviz data based on country.",
    service: "nestjs",
    method: "POST",
    path: "/filings/data/insider_trades",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Stock ticker symbol", example: "AAPL" },
      { name: "country", type: "string", required: true, description: '"india" for NSE/BSE/Trendlyne; other values (e.g. "USA") for US Finviz', example: "USA" },
      { name: "fromDate", type: "string", required: false, description: "Filter start — YYYY-MM-DD or ISO 8601", example: "2026-04-01" },
      { name: "toDate", type: "string", required: false, description: "Filter end — YYYY-MM-DD or ISO 8601", example: "2026-05-01" },
    ],
    bodyExample: { ticker: "AAPL", country: "USA", fromDate: "2026-04-01", toDate: "2026-05-01" },
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote: "Returns markdown table string. India path capped at 100 records without date filters.",
  },
  {
    id: "drhp_filings",
    name: "DRHP Filings",
    shortDescription:
      "Fetch DRHP/prospectus filings for a company by ticker symbol or exact company name. Up to 50 filings with nested document links.",
    service: "nestjs",
    method: "GET",
    path: "/filings/drhp/{ticker}",
    accept: "application/json",
    contentType: null,
    pathParams: [{ name: "ticker", example: "PAYTM", description: "Ticker symbol or exact company name (case-insensitive)" }],
    queryParams: [],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Path param — symbol or company name", example: "PAYTM" },
    ],
    bodyExample: null,
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote: "Returns JSON array with symbol, company_name, form_type, filing_date, source, documents[].",
  },
  {
    id: "combined_financials",
    name: "Combined Financials",
    shortDescription:
      "Fetch combined financial data for a ticker as markdown. India: screener.in; USA: yfinance (income, balance sheet, cash flow, calendar).",
    service: "nestjs",
    method: "POST",
    path: "/filings/combined_financials",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Stock ticker symbol", example: "AAPL" },
      { name: "country", type: "string", required: true, description: 'Allowed: "India", "USA", "United States"', example: "USA" },
      { name: "q", type: "string", required: false, description: 'India only — "consolidated" (default) or "standalone"', example: "consolidated" },
      { name: "period", type: "string", required: false, description: 'USA only — "annual" (default) or "quarterly"', example: "annual" },
    ],
    bodyExample: { ticker: "AAPL", country: "USA", period: "annual" },
    cacheTtl: 300,
    rateLimit: "30/min",
    responseNote: "Returns raw markdown string with financial sections.",
  },
  {
    id: "combined_filings_announcements",
    name: "Combined Filings & Announcements",
    shortDescription:
      "Fetch filings and announcements from BSE/NSE/DRHP/screener.in (India) or SEC via FastAPI (USA). Each item includes isRead flag.",
    service: "nestjs",
    method: "POST",
    path: "/filings/combined_filings_announcements",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Stock ticker symbol", example: "RELIANCE" },
      { name: "country", type: "string", required: true, description: 'Allowed: "India", "USA", "United States"', example: "India" },
      { name: "form", type: "string[]", required: false, description: 'India only — ["concalls"], ["annual_report"], ["earnings_report"], ["all"]', example: '["all"]' },
      { name: "start_date", type: "string", required: false, description: "YYYY-MM-DD; India defaults to 1 year ago", example: "2025-01-01" },
      { name: "end_date", type: "string", required: false, description: "YYYY-MM-DD; India defaults to today", example: "2026-07-15" },
      { name: "email", type: "string", required: false, description: "USA only — forwarded to FastAPI", example: "user@example.com" },
      { name: "company_name", type: "string", required: false, description: "USA only — forwarded to FastAPI", example: "Apple Inc" },
    ],
    bodyExample: { ticker: "RELIANCE", country: "India", form: ["all"], start_date: "2025-01-01", end_date: "2026-07-15" },
    cacheTtl: 120,
    rateLimit: "30/min",
    responseNote: "Returns array sorted by date descending; [] if no data.",
  },
  {
    id: "filings_domestic",
    name: "Filings — Domestic",
    shortDescription:
      "Fetch concall transcripts, annual reports, and earnings report PDFs for an Indian ticker by scraping screener.in.",
    service: "nestjs",
    method: "POST",
    path: "/filings/domestic",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Ticker or stock_identifier (case-insensitive)", example: "RELIANCE" },
      { name: "form", type: "string", required: true, description: '"concalls", "annual_report", "earnings_report", or "all"', example: "concalls" },
    ],
    bodyExample: { ticker: "RELIANCE", form: "concalls" },
    cacheTtl: 300,
    rateLimit: "30/min",
  },
  {
    id: "corp_announcements",
    name: "Corporate Announcements",
    shortDescription:
      "Fetch corporate announcements from BSE (primary), NSE (fallback), and DRHP documents. Results grouped by source.",
    service: "nestjs",
    method: "GET",
    path: "/filings/corp/announcements/{ticker}",
    accept: "application/json",
    contentType: null,
    pathParams: [{ name: "ticker", example: "RELIANCE", description: "Stock ticker (auto-uppercased)" }],
    queryParams: [
      { name: "fromDate", example: "20250101", description: "Start date YYYYMMDD (BSE/NSE only)" },
      { name: "toDate", example: "20260715", description: "End date YYYYMMDD (BSE/NSE only)" },
    ],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Path param", example: "RELIANCE" },
      { name: "fromDate", type: "string", required: true, description: "Query — YYYYMMDD", example: "20250101" },
      { name: "toDate", type: "string", required: true, description: "Query — YYYYMMDD", example: "20260715" },
    ],
    bodyExample: null,
    cacheTtl: 180,
    rateLimit: "30/min",
  },
  {
    id: "financial_tables_markdown",
    name: "Financial Tables (Markdown)",
    shortDescription:
      "Fetch financial summary for an Indian ticker as markdown from screener.in — ratios, shareholding, balance sheet, P&L, quarterly results, peers.",
    service: "nestjs",
    method: "GET",
    path: "/filings/financial_tables/markdown/{ticker}",
    accept: "text/plain",
    contentType: null,
    pathParams: [{ name: "ticker", example: "RELIANCE", description: "Ticker or company name" }],
    queryParams: [{ name: "form", example: "consolidated", description: '"standalone" or consolidated (default)' }],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Path param", example: "RELIANCE" },
      { name: "form", type: "string", required: false, description: 'Query — "standalone" or consolidated', example: "consolidated" },
    ],
    bodyExample: null,
    cacheTtl: 300,
    rateLimit: "30/min",
    responseNote: "Returns raw markdown with Pros & Cons, About, ratios, shareholding, financial tables.",
  },
  {
    id: "street_estimates",
    name: "Street Estimates",
    shortDescription:
      "Get street / analyst estimates for a stock ticker. Query with ticker and country (INDIA or USA).",
    service: "fastapi",
    method: "GET",
    path: "/data/street_estimates",
    accept: "text/plain",
    contentType: null,
    pathParams: [],
    queryParams: [
      { name: "ticker", example: "AAPL", description: "Stock ticker e.g. RELIANCE, AAPL" },
      { name: "country", example: "USA", description: "INDIA or USA" },
    ],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Query — stock ticker e.g. RELIANCE, AAPL", example: "AAPL" },
      { name: "country", type: "string", required: true, description: 'Query — "INDIA" or "USA"', example: "USA" },
    ],
    bodyExample: null,
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote: "Returns text/plain street estimates content for the given ticker and country.",
  },
  {
    id: "ratio_source",
    name: "Ratio Source",
    shortDescription:
      "Look up ratio source data for one or more tickers. Pass matching q, tickers, and countries arrays as query params.",
    service: "fastapi",
    method: "GET",
    path: "/data/ratio_source",
    accept: "text/plain",
    contentType: null,
    pathParams: [],
    queryParams: [
      { name: "q", example: ["PE"], description: "Ratio / metric query strings (repeatable)" },
      { name: "tickers", example: ["AAPL"], description: "Stock tickers (repeatable)" },
      { name: "countries", example: ["USA"], description: "Country per ticker e.g. USA, INDIA (repeatable)" },
    ],
    requestFields: [
      { name: "q", type: "string[]", required: true, description: "Query — ratio/metric names (array)", example: "PE" },
      { name: "tickers", type: "string[]", required: true, description: "Query — ticker symbols (array)", example: "AAPL" },
      { name: "countries", type: "string[]", required: true, description: "Query — country codes matching tickers (array)", example: "USA" },
    ],
    bodyExample: null,
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote: "Returns text/plain ratio source content.",
  },
  {
    id: "fetch_formula",
    name: "Fetch Formula",
    shortDescription: "Fetch formula definitions for one or more metric/query strings.",
    service: "fastapi",
    method: "GET",
    path: "/data/fetch_formula",
    accept: "text/plain",
    contentType: null,
    pathParams: [],
    queryParams: [
      { name: "q", example: ["PE"], description: "Formula / metric query strings (repeatable)" },
    ],
    requestFields: [
      { name: "q", type: "string[]", required: true, description: "Query — formula or metric names (array)", example: "PE" },
    ],
    bodyExample: null,
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote: "Returns text/plain formula content.",
  },
  {
    id: "stock_data",
    name: "Financial Metrics",
    shortDescription:
      "Fetch financial metrics for a single ticker (e.g. stockquote) by country.",
    service: "fastapi",
    method: "POST",
    path: "/stock-data",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "ticker_symbol", type: "string", required: true, description: "Stock ticker symbol", example: "AAPL" },
      { name: "type", type: "string", required: true, description: 'Metric type e.g. "stockquote"', example: "stockquote" },
      { name: "country", type: "string", required: true, description: "Market country e.g. USA or INDIA", example: "USA" },
    ],
    bodyExample: { ticker_symbol: "AAPL", type: "stockquote", country: "USA" },
    cacheTtl: 60,
    rateLimit: "60/min",
    responseNote: "Returns application/json financial metrics payload.",
  },
  {
    id: "stock_data_batch",
    name: "Financial Metrics Batch",
    shortDescription:
      "Fetch financial metrics for multiple tickers in one request. Pass comma-separated countries and tickers (aligned by position).",
    service: "fastapi",
    method: "POST",
    path: "/stock-data/batch",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "country", type: "string", required: true, description: "Comma-separated countries aligned with tickers", example: "USA,USA,INDIA" },
      { name: "ticker_symbol", type: "string", required: true, description: "Comma-separated ticker symbols", example: "AAPL,NVDA,ICICIBANK" },
      { name: "timeout_ms", type: "integer", required: false, description: "Request timeout in milliseconds", example: "1000" },
      { name: "type", type: "string", required: true, description: 'Metric type e.g. "stockquote"', example: "stockquote" },
    ],
    bodyExample: {
      country: "USA,USA,INDIA",
      ticker_symbol: "AAPL,NVDA,ICICIBANK",
      timeout_ms: 1000,
      type: "stockquote",
    },
    cacheTtl: 60,
    rateLimit: "30/min",
    responseNote: "Returns application/json batch metrics payload.",
  },
  {
    id: "market_data",
    name: "Get Stock Data (Market Data)",
    shortDescription:
      "Get historical or intraday market data for a ticker between start and end dates. Optional interval for intraday (1m, 2m, 5m, 15m, 30m, 1h).",
    service: "fastapi",
    method: "GET",
    path: "/market_data",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [
      { name: "ticker", example: "AAPL", description: "Stock ticker" },
      { name: "start", example: "2026-01-01", description: "Start date YYYY-MM-DD" },
      { name: "end", example: "2026-03-31", description: "End date YYYY-MM-DD" },
      { name: "interval", example: "1d", description: "Optional intraday interval: 1m, 2m, 5m, 15m, 30m, 1h" },
      { name: "country", example: "USA", description: "USA (default) or India" },
    ],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Query — stock ticker", example: "AAPL" },
      { name: "start", type: "string", required: true, description: "Query — start date YYYY-MM-DD", example: "2026-01-01" },
      { name: "end", type: "string", required: true, description: "Query — end date YYYY-MM-DD", example: "2026-03-31" },
      { name: "interval", type: "string", required: false, description: "Query — intraday interval if needed", example: "5m" },
      { name: "country", type: "string", required: false, description: "Query — USA (default) or India", example: "USA" },
      { name: "csv", type: "boolean", required: false, description: "Optional CSV override (query or body)", example: "true" },
    ],
    bodyExample: { csv: true },
    cacheTtl: 120,
    rateLimit: "60/min",
    responseNote: "Returns application/json market/OHLC series (or CSV-oriented payload when csv=true).",
  },
  {
    id: "financials",
    name: "Get Financials",
    shortDescription:
      "Get financial statements for a ticker. Path param is the ticker; body selects period (e.g. annual).",
    service: "fastapi",
    method: "POST",
    path: "/financials/{ticker}",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [{ name: "ticker", example: "AAPL", description: "Stock ticker symbol" }],
    queryParams: [],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: "Path param — stock ticker", example: "AAPL" },
      { name: "period", type: "string", required: true, description: 'Body — e.g. "annual" or "quarterly"', example: "annual" },
    ],
    bodyExample: { period: "annual" },
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote: "Returns application/json financials for the ticker and period.",
  },
  {
    id: "drhp_companies",
    name: "DRHP Companies (Unlisted)",
    shortDescription:
      "List all unlisted DRHP/IPO companies with optional search, source filter, and pagination. Filters out companies already listed in the stock table — effectively an upcoming/unlisted IPO list.",
    service: "nestjs",
    method: "GET",
    path: "/filings/drhp",
    accept: "application/json",
    contentType: null,
    pathParams: [],
    queryParams: [
      { name: "search", example: "reliance", description: "Filter by company name or symbol (case-insensitive partial match)" },
      { name: "source", example: "IND", description: 'Filter by source e.g. "US", "IND" (case-insensitive exact match)' },
      { name: "page", example: "1", description: "Page number (default 1)" },
      { name: "limit", example: "50", description: "Results per page (default 50, max 200)" },
    ],
    requestFields: [
      { name: "search", type: "string", required: false, description: "Query — filter by company name or symbol (case-insensitive partial match)", example: "reliance" },
      { name: "source", type: "string", required: false, description: 'Query — filter by source e.g. "US", "IND" (case-insensitive exact match)', example: "IND" },
      { name: "page", type: "integer", required: false, description: "Query — page number (default 1)", example: "1" },
      { name: "limit", type: "integer", required: false, description: "Query — results per page (default 50, max 200)", example: "50" },
    ],
    bodyExample: null,
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote:
      "Returns { data, pagination }. data — unlisted DRHP companies (deduplicated by company_name, sorted by filing_date descending), each with symbol, company_name, form_type, filing_date, open_date, close_date, listing_date, issue_price, subscription, documents[] (sequence, doc_type, description, file_link). pagination — page, limit, total, totalPages.",
  },
  {
    id: "stock_search",
    name: "Stock Search",
    shortDescription:
      "Search stocks by company name or ticker across listed stocks, user/org custom stocks, and unlisted DRHP/IPO companies. Supports filters, sorting, and pagination.",
    service: "nestjs",
    method: "POST",
    path: "/stock/search",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "query", type: "string", required: true, description: 'Search term matched against company_name and ticker (case-insensitive partial match). Pass "" to list without a text filter.', example: "reliance" },
      { name: "filters.exchange", type: "string[]", required: false, description: "Filter by exchange(s)", example: '["NSE"]' },
      { name: "filters.sector", type: "string", required: false, description: "Filter by sector", example: "Energy" },
      { name: "filters.country", type: "string", required: false, description: "Filter by country", example: "INDIA" },
      { name: "sort.field", type: "string", required: false, description: "Field to sort by (applies to listed stocks)", example: "market_cap" },
      { name: "sort.order", type: "string", required: false, description: '"asc" or "desc"', example: "desc" },
      { name: "pagination.limit", type: "integer", required: false, description: "Max results to return", example: "20" },
      { name: "pagination.offset", type: "integer", required: false, description: "Number of results to skip", example: "0" },
    ],
    bodyExample: {
      query: "reliance",
      filters: { exchange: ["NSE"], sector: "Energy", country: "INDIA" },
      sort: { field: "market_cap", order: "desc" },
      pagination: { limit: 20, offset: 0 },
    },
    cacheTtl: 300,
    rateLimit: "60/min",
    responseNote:
      "Returns { total_results, results }. total_results — number of unique tickers. results — object keyed by ticker; each value is a FLAT array where every 3 elements form one (country, company_name, industry) triple (a ticker in multiple countries has 6, 9, ... elements). Merges listed stocks, custom stocks, and DRHP/IPO companies; DRHP entries show industry \"IPO\" and are skipped if the company already exists as a listed/custom stock.",
  },
  {
    id: "llm_chat",
    name: "LLM Chat",
    shortDescription:
      "Send a query to a local or hosted LLM and get the completion back, streamed as NDJSON or as a single JSON response.",
    service: "fastapi",
    method: "POST",
    path: "/query-router",
    accept: "application/x-ndjson",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "query", type: "string", required: true, description: "The prompt to send to the selected LLM", example: "Summarize Apple's latest earnings." },
      { name: "llm_type", type: "string", required: true, description: '"local_llm" or "hosted_llm"', example: "hosted_llm" },
      { name: "stream", type: "boolean", required: false, description: "Default true. If true, streams the response as NDJSON; if false, returns the full response as JSON.", example: "false" },
      { name: "temperature", type: "float", required: false, description: "Sampling temperature (defaults to config value)", example: "0.7" },
      { name: "max_tokens", type: "integer", required: false, description: "Maximum tokens in the response (defaults to config value)", example: "1024" },
    ],
    bodyExample: {
      query: "Summarize Apple's latest earnings.",
      llm_type: "hosted_llm",
      stream: false,
      temperature: 0.7,
      max_tokens: 1024,
    },
    cacheTtl: 0,
    rateLimit: "30/min",
    responseNote:
      'If stream=true: NDJSON (application/x-ndjson), each line { "text": "..." } or { "error": "..." } on failure. If stream=false: a single JSON object { "text": "..." }.',
  },
  {
    id: "super_investors_list",
    name: "Super Investors List",
    shortDescription:
      'List well-known ("super") investors and their basic profile. Scraped live from finology.in.',
    service: "nestjs",
    method: "GET",
    path: "/super-investors",
    accept: "application/json",
    contentType: null,
    pathParams: [],
    queryParams: [],
    requestFields: [],
    bodyExample: null,
    responseExample: {
      count: 1,
      investors: [
        {
          name: "Ashish Kacholia",
          slug: "ashish-kacholia",
          bio: null,
          imageUrl: null,
        },
      ],
    },
    cacheTtl: 300,
    rateLimit: "30/min",
    responseNote:
      "Returns { count, investors }. count — number of investors. investors — each with name, slug (use as the {slug} path param for the portfolio endpoint), bio (string|null), imageUrl (string|null).",
  },
  {
    id: "super_investor_portfolio",
    name: "Super Investor Portfolio",
    shortDescription:
      "Get a super investor's portfolio with quarterly holdings by company. Scraped live from finology.in.",
    service: "nestjs",
    method: "GET",
    path: "/super-investors/{slug}",
    accept: "application/json",
    contentType: null,
    pathParams: [
      { name: "slug", example: "ashish-kacholia", description: 'Investor slug e.g. "ashish-kacholia". Only [a-z0-9-] allowed (400 if invalid); 404 if not found.' },
    ],
    queryParams: [],
    requestFields: [
      { name: "slug", type: "string", required: true, description: 'Path param — investor slug e.g. "ashish-kacholia". Only [a-z0-9-] allowed (400 if invalid); 404 if not found.', example: "ashish-kacholia" },
    ],
    bodyExample: null,
    responseExample: {
      name: "Ashish Kacholia",
      slug: "ashish-kacholia",
      netWorthCr: 2500,
      activeStocks: 40,
      totalStocks: 45,
      quarters: ["Sep 2025", "Jun 2025"],
      holdings: [
        {
          company: "Example Ltd",
          companySlug: "example-ltd",
          quarterlyHoldings: { "Sep 2025": 5.2, "Jun 2025": 4.8 },
          valueCr: 130,
        },
      ],
    },
    cacheTtl: 300,
    rateLimit: "30/min",
    responseNote:
      'Returns { name, slug, netWorthCr (₹ crore, number|null), activeStocks (integer|null), totalStocks (integer|null), quarters, holdings }. quarters — quarter labels (e.g. "Sep 2025") used as keys in each holding\'s quarterlyHoldings. holdings — each with company, companySlug (string|null), quarterlyHoldings (object keyed by quarter label → holding % or null), valueCr (number|null). Numeric fields are null where the source shows "-"/missing.',
  },
  {
    id: "free_float_market_cap",
    name: "Free Float Market Cap",
    shortDescription:
      "Fetch the free float market cap (and total market cap) for an Indian listed stock, in ₹ crore. Sourced live from BSE.",
    service: "nestjs",
    method: "POST",
    path: "/filings/free_float_market_cap",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [],
    queryParams: [],
    requestFields: [
      { name: "ticker", type: "string", required: true, description: 'NSE symbol (e.g. "TCS"), BSE security_id, or numeric BSE scrip code (e.g. "532540"). 400 if empty; 404 if no BSE scrip maps or BSE returns no market-cap field; 502 if the BSE fetch fails.', example: "TCS" },
    ],
    bodyExample: { ticker: "TCS" },
    responseExample: {
      symbol: "TCS",
      scripCode: "532540",
      freeFloatMarketCap: 456789.12,
      totalMarketCap: 1234567.89,
      currency: "INR",
      unit: "Cr",
      source: "BSE",
      asOf: "2026-08-26T00:00:00.000Z",
    },
    cacheTtl: 300,
    rateLimit: "30/min",
    responseNote:
      'Returns { symbol, scripCode, freeFloatMarketCap, totalMarketCap, currency, unit, source, asOf }. symbol — input trimmed/uppercased; scripCode — resolved BSE scrip code; freeFloatMarketCap/totalMarketCap — ₹ crore (number|null); currency always "INR"; unit always "Cr"; source always "BSE"; asOf — ISO timestamp when fetched.',
  },
  {
    id: "sheet_template_run",
    name: "Sheet Template — Run",
    shortDescription:
      "Run a saved sheet template for new companies. Creates a new sheet from the template, clears formula cells, injects the given values, and queues one agent job per formula cell. Flow: run → poll execution → get full result.",
    service: "nestjs",
    method: "POST",
    path: "/sheets/templates/{templateId}/run",
    accept: "application/json",
    contentType: "application/json",
    pathParams: [
      { name: "templateId", example: "53", description: "Sheet template id" },
    ],
    queryParams: [],
    requestFields: [
      { name: "templateId", type: "string", required: true, description: "Path param — sheet template id", example: "53" },
      { name: "workbook_name", type: "string", required: true, description: "Name for the generated sheet", example: "Company Overview" },
      { name: "formula_inputs", type: "{ cellId: string; value: string }[]", required: true, description: 'Formula cells to run. cellId (e.g. "B1") must exist in the template; value is the company ticker/name for that cell', example: '[{ "cellId": "B1", "value": "RELIANCE" }]' },
      { name: "cell_inputs", type: "{ cellId: string; value: string }[]", required: false, description: "Plain text cells (labels, headers) written as-is", example: '[{ "cellId": "A1", "value": "RELIANCE" }]' },
    ],
    bodyExample: {
      workbook_name: "Company Overview",
      formula_inputs: [
        { cellId: "B1", value: "RELIANCE" },
        { cellId: "B2", value: "TCS" },
      ],
      cell_inputs: [
        { cellId: "A1", value: "RELIANCE" },
        { cellId: "A2", value: "TCS" },
      ],
    },
    responseExample: {
      id: 321,
      run_id: "321",
      template_id: 53,
      user_index: 153,
      workbook_name: "Company Overview",
      sheet_names: ["Sheet1"],
      active_sheet_name: "Sheet1",
      ticker: "RELIANCE",
      queued_formula_count: 2,
      runs: [
        { cellId: "B1", taskId: "uuid-1" },
        { cellId: "B2", taskId: "uuid-2" },
      ],
    },
    cacheTtl: 0,
    rateLimit: "—",
    responseNote:
      "201 Created. Use run_id with the execution and result endpoints below. runs — one { cellId, taskId } per queued formula cell. Only the user who created the run can read it (others get 404); service tokens must pass USER_INDEX in the run request.",
  },
  {
    id: "sheet_template_run_execution",
    name: "Sheet Template — Run Execution Status",
    shortDescription:
      "Lightweight poll of a template run's per-cell progress. Use this for polling (every 3–5 seconds) until status is \"completed\".",
    service: "nestjs",
    method: "GET",
    path: "/sheets/templates/run/{runId}/execution",
    accept: "application/json",
    contentType: null,
    pathParams: [
      { name: "runId", example: "321", description: "run_id returned by POST /sheets/templates/{templateId}/run" },
    ],
    queryParams: [],
    requestFields: [
      { name: "runId", type: "string", required: true, description: "Path param — run_id from the run response", example: "321" },
    ],
    bodyExample: null,
    responseExample: {
      run_id: "321",
      sheet_id: 321,
      template_id: 53,
      status: "completed",
      execution_complete: true,
      total_formula_count: 2,
      queued_formula_count: 0,
      running_formula_count: 0,
      completed_formula_count: 2,
      failed_formula_count: 0,
      expired_formula_count: 0,
      cells: [
        {
          cellId: "B1",
          taskId: "uuid-1",
          status: "completed",
          result: "Reliance Industries' products...",
          message: null,
          queuedAt: 1792155974596,
          startedAt: null,
          finishedAt: 1792156088181,
        },
      ],
    },
    cacheTtl: 0,
    rateLimit: "poll every 3–5s",
    responseNote:
      'status — "pending" (cells still queued or running) or "completed" (all cells finished, success or fail). Cell status — "queued", "running", "completed", "failed" (see message), or "expired" (job status expired). Job status is kept for 1 hour after a cell finishes; after that the cell status is derived from the saved cell value: has value → completed, error value → failed, empty → expired. The run manifest is kept for 6 hours after creation — 404 means the run was not found, has expired, or belongs to another user.',
  },
  {
    id: "sheet_template_run_result",
    name: "Sheet Template — Run Result",
    shortDescription:
      "Get the full result of a template run: execution status plus the complete sheet output with every cell value. Call once the execution poll reports \"completed\".",
    service: "nestjs",
    method: "GET",
    path: "/sheets/templates/run/{runId}",
    accept: "application/json",
    contentType: null,
    pathParams: [
      { name: "runId", example: "321", description: "run_id returned by POST /sheets/templates/{templateId}/run" },
    ],
    queryParams: [],
    requestFields: [
      { name: "runId", type: "string", required: true, description: "Path param — run_id from the run response", example: "321" },
    ],
    bodyExample: null,
    responseExample: {
      run_id: "321",
      sheet_id: 321,
      template_id: 53,
      workbook_name: "Company Overview",
      ticker: "RELIANCE",
      status: "completed",
      execution_complete: true,
      total_formula_count: 2,
      completed_formula_count: 2,
      failed_formula_count: 0,
      active_sheet_name: "Sheet1",
      cells: [
        { cellId: "B1", status: "completed", result: "Reliance Industries' products..." },
        { cellId: "B2", status: "completed", result: "TCS provides enterprise software..." },
      ],
      output: [
        {
          name: "Sheet1",
          active: true,
          cells: {
            A1: "RELIANCE",
            A2: "TCS",
            B1: "Business Area",
            C1: "Products",
          },
        },
      ],
    },
    cacheTtl: 0,
    rateLimit: "—",
    responseNote:
      "Same execution fields as the execution endpoint, plus workbook_name, ticker, active_sheet_name, and output — one entry per sheet with { name, active, cells } where cells maps cell id → value. Check cells for status \"failed\" and surface each cell's message. 404 if the run is not found, expired (>6 hours), or belongs to another user.",
  },
];

/** Sheet Template Run endpoints, in call order (run → poll execution → full result). */
export const SHEET_TEMPLATE_RUN_API_IDS = [
  "sheet_template_run",
  "sheet_template_run_execution",
  "sheet_template_run_result",
];

export const DASHBOARD_SUMMARISER_API_IDS = ["dashboard_ask", "dashboard_ask_poll"];

export const getDashboardSummariserApis = () =>
  MUNSHOT_DATA_APIS.filter((api) => DASHBOARD_SUMMARISER_API_IDS.includes(api.id));

/** Resolve host from service key. */
export const getApiHost = (api) => BASE_URLS[api.service] || BASE_URLS.nestjs;

export const buildCurl = (api) => {
  const host = getApiHost(api);
  const urlPath = api.path.replace(/\{(\w+)\}/g, (_, key) => {
    const param = api.pathParams.find((p) => p.name === key);
    return param?.example || key;
  });

  let url = `${host}${urlPath}`;
  if (api.queryParams?.length) {
    const parts = [];
    api.queryParams.forEach((q) => {
      if (q.example == null || q.example === "") return;
      if (Array.isArray(q.example)) {
        q.example.forEach((item) => {
          parts.push(`${encodeURIComponent(q.name)}=${encodeURIComponent(item)}`);
        });
      } else {
        parts.push(`${encodeURIComponent(q.name)}=${encodeURIComponent(q.example)}`);
      }
    });
    if (parts.length) url += `?${parts.join("&")}`;
  }

  if (api.bodyExample) {
    return `curl -X '${api.method}' \\
  '${url}' \\
  -H 'accept: ${api.accept}' \\
  -H 'Authorization: Bearer ${TOKEN_PLACEHOLDER}' \\
  -H 'Content-Type: ${api.contentType}' \\
  -d '${JSON.stringify(api.bodyExample)}'`;
  }

  return `curl -X '${api.method}' \\
  '${url}' \\
  -H 'accept: ${api.accept}' \\
  -H 'Authorization: Bearer ${TOKEN_PLACEHOLDER}'`;
};

const formatRequestFields = (fields) => {
  if (!fields?.length) return "None";
  return fields
    .map((f) => {
      const req = f.required ? "required" : "optional";
      const ex = f.example ? ` — example: \`${f.example}\`` : "";
      return `- \`${f.name}\` (${f.type}, ${req}): ${f.description}${ex}`;
    })
    .join("\n");
};

/** One-shot copy: datasource id, description, request fields, cURL. */
export const buildApiCopyBlock = (api) => {
  const host = getApiHost(api);
  const sections = [
    `# ${api.name} (\`${api.id}\`)`,
    "",
    api.shortDescription,
    "",
    `**Service:** ${api.service} → \`${host}\``,
    `**Endpoint:** ${api.method} ${api.path}`,
    "",
    "## Request fields",
    formatRequestFields(api.requestFields),
    "",
  ];

  if (api.bodyExample) {
    sections.push("## Body example", "", "```json", JSON.stringify(api.bodyExample, null, 2), "```", "");
  }

  if (api.responseExample) {
    sections.push("## Response example", "", "```json", JSON.stringify(api.responseExample, null, 2), "```", "");
  }

  if (api.responseNote) {
    sections.push("## Response", api.responseNote, "");
  }

  sections.push(
    "## cURL",
    "",
    "```bash",
    buildCurl(api),
    "```",
    ""
  );

  return sections.join("\n");
};

export const buildFullMarkdownDoc = () => {
  const header = `# Munshot Datasource Registry — API Reference

> **For Tarandeep / skills authors:** Add this file (or individual sections) to the dashboard skill \`datasource-registry.md\` reference. Always use \`<YOUR_SESSION_TOKEN>\` or the host session token.

${AUTH_NOTE}

${REGISTRY_INTRO}

**Base URLs:**
- \`fastapi\` → \`${BASE_URLS.fastapi}\`
- \`nestjs\` → \`${BASE_URLS.nestjs}\`

---
`;

  const body = MUNSHOT_DATA_APIS.map((api) => buildApiCopyBlock(api)).join("\n---\n\n");
  return `${header}\n${body}\n`;
};
